import * as Dialog from '@radix-ui/react-dialog';
import { Dispatch, SetStateAction } from 'react';

import {
  ScaleItem,
  SCORE_SCALE,
  ScoreScaleItem,
} from '@/components/TokenProfile/TokenLiquidityScore/ScoreTooltip';

import { useFiatDisplay } from '@/hooks/useFiatDisplay';

import { buttons } from '@/styles/buttons';
import { fontStyles } from '@/styles/typography';

import { TokenAvatar } from '@/ui/Avatars/TokenAvatar';
import { XIcon } from '@/ui/Icons/XIcon';
import { Modal } from '@/ui/Modal';

import { TokenResult } from '@/utils/0x/token-registry.types';
import { getPriceImpactThreshold } from '@/utils/liquidityScore';
import { isZoraCreatorCoin, isZoraToken, type ZoraType } from '@/utils/zora';

interface Props {
  activeToken: TokenResult;
  currentScore: number | undefined;
  isOpen: boolean;
  onOpenChange: Dispatch<SetStateAction<boolean>>;
}

export const LiquidityScoreModal = ({ activeToken, currentScore, isOpen, onOpenChange }: Props) => {
  const { formatFiat } = useFiatDisplay();
  const formatAmount = (amount: number) => formatFiat(amount, false, false);
  let zoraType: ZoraType;
  if (isZoraToken(activeToken)) {
    zoraType = isZoraCreatorCoin(activeToken) ? 'creator' : 'post';
  }
  const threshold = getPriceImpactThreshold(zoraType);

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => onOpenChange(false)}
      onOpenChange={onOpenChange}
      variant="LARGE"
    >
      <div className="text-token-gray-1600 h-full overflow-hidden flex flex-col">
        <div className="pb-2 pt-6 px-4 tablet:px-6 sticky top-0 z-10 w-full">
          <div className="mb-4 flex justify-between w-full">
            <Dialog.Title className={fontStyles({ variant: 'H5', class: 'text-token-gray-1600' })}>
              Liquidity Score
            </Dialog.Title>
            <Dialog.Close
              className={buttons({
                color: 'transparent',
                shape: 'rounded',
                size: 'small',
                iconOnly: true,
                class: 'text-token-gray-1600 -mt-2 -mr-2',
              })}
              aria-label="close liquidity score dialog"
            >
              <XIcon className="size-4" />
            </Dialog.Close>
          </div>
        </div>

        <div className="overflow-y-auto overflow-x-hidden px-4 tablet:px-6">
          <div className="flex items-center pb-2 gap-1">
            <TokenAvatar
              address={activeToken.address}
              chainId={activeToken.chainId}
              showChainLogo={false}
              size={16}
            />
            <div className="flex items-center justify-center">
              <span
                className={fontStyles({
                  variant: 'LABEL_MD',
                  class: 'inline-flex text-token-gray-1600',
                })}
              >
                {activeToken.name}
                <span
                  className={fontStyles({
                    variant: 'CAPTION',
                    class: 'inline-flex mt-px ml-1 text-token-gray-800',
                  })}
                >
                  {activeToken.symbol}
                </span>
              </span>
            </div>
          </div>
          <div
            className={fontStyles({ variant: 'BODY_SM', class: 'text-token-gray-800 pt-2 pb-4' })}
          >
            The liquidity score measures a token’s {threshold}% depth across DEX markets at a
            specific time. It may not always be accurate, as high-liquidity tokens can become
            illiquid within a single block. Use it alongside other factors, do your own research,
            and consult multiple sources.
          </div>
          <ul className="mb-6">
            <li
              className={fontStyles({
                variant: 'LABEL_MD',
                class: 'flex justify-between p-2 text-token-gray-1600',
              })}
            >
              Score {threshold}% Depth
            </li>
            {SCORE_SCALE.map(({ amount, icon, score }: ScoreScaleItem) => (
              <ScaleItem
                key={`score-${score}`}
                amount={amount}
                score={score}
                currentScore={currentScore}
                icon={icon}
                formatAmount={formatAmount}
                className={fontStyles({
                  variant: 'BODY_MD',
                  class:
                    'px-2 py-3 text-token-gray-800 border-t border-t-token-gray-300 h-[unset] m-[unset]',
                })}
              />
            ))}
          </ul>
        </div>
      </div>
    </Modal>
  );
};
